import { useState, useMemo, useEffect } from "react";
import Icon from "../components/Icon";
import { ALFA_ESTADO, colorUsuario, conAlfa, diasDelPeriodo, esFinde, isoLocal, vacacionAprobada } from "../utils";
import { clicable } from "../components/ClicableTeclado.jsx";

const MONTHS_ES = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"
];
const DAYS_INI = ["L","M","X","J","V","S","D"];

// "sin-tipo" es lo que llega de la Vista general de Odoo, que enseña quién falta
// pero no por qué. No se traduce a "Vacaciones": esa ausencia puede ser una baja.
const TIPO_LABEL = { vacaciones:"Vacaciones", permiso:"Permiso", baja:"Baja",
                     "sin-tipo":"Ausencia (tipo no visible)" };
// Lo aprobado no lleva etiqueta: es el caso normal y no hace falta decirlo.
const ETIQUETA_ESTADO = { borrador:"borrador, sin solicitar", solicitada:"pendiente de aprobar",
                          rechazada:"rechazada por RRHH" };

// Una persona de una celda, en una frase: «Ana, vacaciones, pendiente de aprobar (Odoo)».
const textoPersona = v => [v.user_name, (TIPO_LABEL[v.tipo] || v.tipo || "").toLowerCase(),
  ETIQUETA_ESTADO[v.estado], v.odoo_tipo_nombre && `(${v.odoo_tipo_nombre})`].filter(Boolean).join(", ");

// Nombre accesible de una celda del calendario anual: la fecha completa, el festivo y
// TODA la gente que falta ese día (la celda solo pinta dos colores; el +N cuenta el resto).
// El title lleva lo mismo, pero no se lee con teclado ni en todos los lectores.
export function etiquetaCelda(iso, fest, gente = []) {
  const [a, m, d] = iso.split("-").map(Number);
  return [`${d} de ${MONTHS_ES[m - 1].toLowerCase()} de ${a}`,
          fest && `festivo: ${fest}`,
          ...gente.map(textoPersona)].filter(Boolean).join("; ");
}

// Pseudo-departamento para quien no está en ningún equipo: sin él, esas personas
// serían inalcanzables desde el filtro.
const SIN_DEPTO = "__sin__";

// Rejilla de un mes: semanas de lunes a domingo, con huecos fuera del mes
function gridMes(year, month) {
  const primero = new Date(year, month, 1);
  const offset  = (primero.getDay() + 6) % 7;
  const total   = new Date(year, month + 1, 0).getDate();
  const celdas  = [];
  for (let i = 0; i < offset; i++) celdas.push(null);
  for (let d = 1; d <= total; d++) celdas.push(isoLocal(new Date(year, month, d)));
  while (celdas.length % 7 !== 0) celdas.push(null);
  return celdas;
}

export default function VacacionesView({
  vacaciones = [], users = [], teams = [], festivos = {}, currentUser,
  year, setYear, isAdmin = false,
  onNuevoPeriodo, onEditarPeriodo, onImportar,
}) {
  // ── Quién pertenece a qué ──────────────────────────────────────────────────
  const miembrosDe = useMemo(() => {
    const m = {};
    teams.forEach(t => { m[String(t.id)] = (t.members || []).map(String); });
    return m;
  }, [teams]);

  const conDepto = useMemo(() => {
    const s = new Set();
    teams.forEach(t => (t.members || []).forEach(u => s.add(String(u))));
    return s;
  }, [teams]);

  const sinDepto = useMemo(
    () => users.filter(u => !conDepto.has(String(u.id))).map(u => String(u.id)),
    [users, conDepto]);

  // El departamento de origen de quien está mirando: es la selección por defecto
  const misEquipos = useMemo(
    () => teams.filter(t => (t.members || []).map(String).includes(String(currentUser?.id)))
               .map(t => String(t.id)),
    [teams, currentUser]);

  // ── Selección ──────────────────────────────────────────────────────────────
  // La lista de personas se DERIVA de los departamentos marcados: marcar uno
  // añade a los suyos y desmarcarlo los quita. `ocultas` y `extras` son los
  // ajustes finos que el usuario hace encima de esa selección.
  const [equiposOn, setEquiposOn] = useState(() => new Set());
  const [ocultas,   setOcultas  ] = useState(() => new Set());
  const [extras,    setExtras   ] = useState(() => new Set());
  const [busca,     setBusca    ] = useState("");

  // Los equipos llegan por API después del primer render, así que la selección
  // por defecto se fija en cuanto están disponibles, una sola vez.
  const [iniciado, setIniciado] = useState(false);
  useEffect(() => {
    if (iniciado || !teams.length) return;
    setEquiposOn(new Set(misEquipos.length ? misEquipos : [SIN_DEPTO]));
    setIniciado(true);
  }, [teams, misEquipos, iniciado]);

  const toggleEquipo = id => setEquiposOn(p => {
    const n = new Set(p); const k = String(id);
    if (n.has(k)) {
      n.delete(k);
      // Al quitar un departamento se olvidan los ajustes finos de su gente, para
      // que volver a marcarlo lo devuelva al estado limpio y no a medio apagar.
      const suyos = k === SIN_DEPTO ? sinDepto : (miembrosDe[k] || []);
      setOcultas(o => { const m = new Set(o); suyos.forEach(u => m.delete(u)); return m; });
      setExtras (e => { const m = new Set(e); suyos.forEach(u => m.delete(u)); return m; });
    } else n.add(k);
    return n;
  });

  const togglePersona = id => setOcultas(p => {
    const n = new Set(p); const k = String(id);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  // Personas que el filtro pone sobre la mesa: las de los departamentos marcados
  // más las añadidas a mano desde el buscador.
  const candidatos = useMemo(() => {
    const s = new Set(extras);
    equiposOn.forEach(k => (k === SIN_DEPTO ? sinDepto : (miembrosDe[k] || [])).forEach(u => s.add(u)));
    return s;
  }, [equiposOn, extras, miembrosDe, sinDepto]);

  const visibleUser = uid => candidatos.has(String(uid)) && !ocultas.has(String(uid));

  const listaCandidatos = useMemo(
    () => users.filter(u => candidatos.has(String(u.id)))
               .sort((a,b) => (a.name||"").localeCompare(b.name||"")),
    [users, candidatos]);

  // El color se reparte entre las personas que se ven, no entre los cien y pico
  // usuarios de la base: sobre el total, seis personas cualesquiera pueden caer
  // en tonos vecinos. Cambiar el filtro reordena los colores, pero la leyenda
  // está justo al lado.
  const paleta = listaCandidatos;

  // Las personas que de verdad se estan viendo: los departamentos marcados menos
  // las apagadas a mano. Es lo que se le ofrece al dar de alta un periodo, para
  // no plantar delante los cien y pico usuarios de la empresa cuando lo que
  // tienes en pantalla son seis.
  const personasVisibles = useMemo(
    () => listaCandidatos.filter(u => !ocultas.has(String(u.id))),
    [listaCandidatos, ocultas]);

  // El buscador solo ofrece a quien no está ya en la lista
  const resultados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return [];
    return users.filter(u => !candidatos.has(String(u.id)) && (u.name||"").toLowerCase().includes(q))
                .slice(0, 8);
  }, [busca, users, candidatos]);

  // ── Datos ya filtrados ─────────────────────────────────────────────────────
  const visibles = useMemo(() => vacaciones.filter(v => visibleUser(v.user_id)),
    [vacaciones, candidatos, ocultas]); // eslint-disable-line

  // Los fines de semana y los festivos dentro de un periodo NO se pintan como
  // vacaciones: no se consumen, así que la vista coincide con el recuento.
  const porDia = useMemo(() => {
    const idx = {};
    visibles.forEach(v => diasDelPeriodo(v.fecha, v.fecha_fin).forEach(d => {
      if (esFinde(d) || festivos[d]) return;
      (idx[d] = idx[d] || []).push(v);
    }));
    return idx;
  }, [visibles, festivos]);

  // Recuento del año: se cuentan días laborables, porque un fin de semana o un
  // festivo dentro del periodo no gasta vacaciones.
  // Lo pendiente se cuenta APARTE: todavía no ha gastado nada, y sumarlo al
  // total daría por consumidos días que RRHH aún puede denegar.
  const resumen = useMemo(() => {
    const m = {};
    visibles.forEach(v => {
      const u = m[String(v.user_id)] || (m[String(v.user_id)] =
        { user_id:v.user_id, name:v.user_name, laborables:0, pendientes:0 });
      const campo = vacacionAprobada(v) ? "laborables" : "pendientes";
      diasDelPeriodo(v.fecha, v.fecha_fin).forEach(d => {
        if (!d.startsWith(String(year))) return;
        if (!esFinde(d) && !festivos[d]) u[campo]++;
      });
    });
    return Object.values(m).sort((a,b) => (b.laborables + b.pendientes) - (a.laborables + a.pendientes));
  }, [visibles, year, festivos]);

  const hoy = isoLocal(new Date());
  // El alta parte del año que se está mirando: si estás en 2027, proponer una
  // fecha de 2026 obliga a navegar el selector doce meses hacia delante.
  const fechaBase = year === new Date().getFullYear() ? hoy : `${year}-01-01`;

  // Trama por tipo, para distinguir de un vistazo una baja de unas vacaciones.
  // La dirección de las rayas es lo que las separa: las del festivo van a 45°.
  const tramaTipo = (tipo, color) => {
    if (tipo === "baja")    return `repeating-linear-gradient(135deg, ${color} 0 5px, rgba(0,0,0,.38) 5px 10px)`;
    if (tipo === "permiso") return `repeating-linear-gradient(90deg, ${color} 0 5px, rgba(255,255,255,.42) 5px 8px)`;
    return "none";   // vacaciones: color liso
  };

  // Al pulsar un día se edita el primer periodo propio; si no hay ninguno
  // editable, se abre uno nuevo con la fecha ya puesta.
  const clickDia = iso => {
    const propio = (porDia[iso] || []).find(v => v.can_edit);
    if (propio) onEditarPeriodo?.(propio, personasVisibles);
    else onNuevoPeriodo?.(iso, personasVisibles);
  };

  const nDeptos = teams.length + (sinDepto.length ? 1 : 0);
  const todosLosDeptos = () => setEquiposOn(new Set([...teams.map(t=>String(t.id)), ...(sinDepto.length?[SIN_DEPTO]:[])]));
  const ningunDepto    = () => { setEquiposOn(new Set()); setOcultas(new Set()); setExtras(new Set()); };

  const filaDepto = (key, nombre, color, n, esMio) => {
    const on = equiposOn.has(key);
    return (
      <label key={key} style={{...filaChkS, background: esMio ? "var(--accent-bg)" : "transparent",
        borderRadius:6, padding:"4px 6px", margin:"0 -6px"}}>
        <input type="checkbox" checked={on} onChange={()=>toggleEquipo(key)}
          style={{accentColor:color,width:14,height:14,cursor:"pointer",flexShrink:0}}/>
        <span style={{flex:1,fontWeight:on?600:400,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
          {nombre}
        </span>
        {esMio && <Icon name="person-check-fill" size={10} color="var(--accent)"/>}
        <span style={{fontSize:11,color:"var(--text3)",flexShrink:0}}>{n}</span>
      </label>
    );
  };

  return (
    <div className="side-split" style={{display:"flex",gap:16,flex:1,minHeight:0}}>

      {/* ── Filtros y resumen ── */}
      <aside className="side-stack" style={{width:225,flexShrink:0,display:"flex",flexDirection:"column",gap:12,overflowY:"auto"}}>

        <div style={cajaS}>
          <div style={{display:"flex",alignItems:"baseline",gap:6,marginBottom:8}}>
            <span style={{...tituloS,marginBottom:0,flex:1}}>Departamentos</span>
            <button className="btn" onClick={todosLosDeptos} style={linkS}>todos</button>
            <span style={{color:"var(--border)"}}>·</span>
            <button className="btn" onClick={ningunDepto} style={linkS}>ninguno</button>
          </div>
          <div style={{maxHeight:230,overflowY:"auto",margin:"0 -2px",padding:"0 2px"}}>
            {teams.map(t => filaDepto(
              String(t.id), t.name, t.color || "var(--accent)",
              (t.members||[]).length, misEquipos.includes(String(t.id))))}
            {sinDepto.length > 0 && filaDepto(
              SIN_DEPTO, "Sin departamento", "var(--text3)", sinDepto.length,
              !misEquipos.length && !conDepto.has(String(currentUser?.id)))}
          </div>
          <div style={{fontSize:11,color:"var(--text3)",marginTop:8,lineHeight:1.4}}>
            {equiposOn.size} de {nDeptos} · marcar un departamento añade a su gente.
          </div>
        </div>

        <div style={cajaS}>
          <div style={{...tituloS,marginBottom:8}}>Personas ({listaCandidatos.length - ocultas.size})</div>

          {listaCandidatos.length ? (
            <div style={{maxHeight:260,overflowY:"auto",margin:"0 -2px",padding:"0 2px"}}>
              {listaCandidatos.map(u => {
                const off = ocultas.has(String(u.id));
                return (
                  <label key={u.id} style={filaChkS}>
                    <input type="checkbox" checked={!off} onChange={()=>togglePersona(u.id)}
                      style={{accentColor:colorUsuario(u.id,paleta),width:14,height:14,cursor:"pointer",flexShrink:0}}/>
                    <span style={{width:9,height:9,borderRadius:3,flexShrink:0,
                      background:colorUsuario(u.id,paleta), opacity:off?.35:1}}/>
                    <span style={{flex:1,fontWeight:off?400:600,opacity:off?.55:1,
                      whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                      {u.name}
                    </span>
                  </label>
                );
              })}
            </div>
          ) : (
            <div style={{fontSize:11,color:"var(--text3)",lineHeight:1.45}}>
              Ningún departamento marcado. Marca uno arriba o busca a alguien.
            </div>
          )}

          {/* Añadir a alguien concreto sin marcar su departamento entero */}
          <div style={{marginTop:9,paddingTop:9,borderTop:"1px solid var(--border2)"}}>
            <input aria-label="Añadir a alguien a la vista" value={busca} onChange={e=>setBusca(e.target.value)}
              placeholder="Añadir a alguien…"
              style={{width:"100%",padding:"6px 9px",borderRadius:7,border:"1px solid var(--border)",
                fontSize:11.5,fontFamily:"var(--font)",boxSizing:"border-box",
                background:"var(--surface2)",color:"var(--text)"}}/>
            {resultados.map(u => (
              <button key={u.id} className="btn"
                onClick={()=>{ setExtras(p=>new Set(p).add(String(u.id))); setBusca(""); }}
                style={{display:"flex",alignItems:"center",gap:7,width:"100%",marginTop:4,
                  padding:"5px 8px",borderRadius:7,border:"1px solid var(--border)",
                  background:"var(--surface)",cursor:"pointer",fontSize:11.5,
                  color:"var(--text2)",fontFamily:"var(--font)",textAlign:"left"}}>
                <Icon name="plus-lg" size={10} color="var(--accent)"/>
                <span style={{flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{u.name}</span>
              </button>
            ))}
            {busca.trim() && !resultados.length && (
              <div style={{fontSize:11,color:"var(--text3)",marginTop:5}}>Sin resultados nuevos.</div>
            )}
          </div>
        </div>

        <div style={cajaS}>
          <div style={tituloS}>Días en {year}</div>
          {resumen.length ? resumen.map(r => (
            <div key={r.user_id} style={{display:"flex",alignItems:"center",gap:7,padding:"4px 0",fontSize:11.5}}>
              <span style={{width:9,height:9,borderRadius:3,background:colorUsuario(r.user_id,paleta),flexShrink:0}}/>
              <span style={{flex:1,color:"var(--text2)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.name}</span>
              <b style={{color:"var(--text)"}}>{r.laborables}</b>
              {r.pendientes > 0 && (
                <span title={`${r.pendientes} día(s) pendientes de aprobar`}
                  style={{fontSize:11,color:"var(--text3)",fontWeight:700}}>+{r.pendientes}</span>
              )}
            </div>
          )) : <div style={{fontSize:11,color:"var(--text3)"}}>Sin periodos en la selección.</div>}
          <div style={{fontSize:11,color:"var(--text3)",marginTop:8,lineHeight:1.4}}>
            Solo días laborables: fines de semana y festivos no descuentan.
            {resumen.some(r => r.pendientes > 0) && (
              <> El <b>+N</b> son días aún sin aprobar por RRHH: no descuentan
              todavía.</>
            )}
          </div>

          {/* Sin esto, los tres aspectos de una celda no significan nada. */}
          <div style={{marginTop:11,paddingTop:9,borderTop:"1px solid var(--border)"}}>
            {[
              { alfa: ALFA_ESTADO.borrador,   rayas:true,  t:"Borrador",   d:"solo registrado aquí" },
              { alfa: ALFA_ESTADO.solicitada, rayas:false, t:"Solicitada", d:"esperando a RRHH" },
              { alfa: ALFA_ESTADO.aprobada,   rayas:false, t:"Aprobada",   d:"firme, no exige horas" },
            ].map(x => (
              <div key={x.t} style={{display:"flex",alignItems:"center",gap:7,padding:"2.5px 0",fontSize:11}}>
                <span style={{width:13,height:13,borderRadius:3,flexShrink:0,
                  background: conAlfa("hsl(205, 70%, 45%)", x.alfa),
                  outline: x.rayas ? "1.5px dashed hsl(205, 70%, 45%)" : "none",
                  outlineOffset: x.rayas ? "-1.5px" : undefined,
                  boxShadow: !x.rayas && x.alfa < 1 ? "inset 0 0 0 1.5px hsl(205, 70%, 45%)" : "none"}}/>
                <b style={{color:"var(--text2)"}}>{x.t}</b>
                <span style={{color:"var(--text3)"}}>· {x.d}</span>
              </div>
            ))}
          </div>
        </div>
      </aside>

      {/* ── Año ── */}
      <div style={{flex:1,display:"flex",flexDirection:"column",gap:12,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button aria-label="Año anterior" onClick={()=>setYear(y=>y-1)} className="btn" style={navBtnS}>
            <Icon name="chevron-left" size={14} color="var(--text2)"/>
          </button>
          <div style={{flex:1,textAlign:"center",fontWeight:800,fontSize:16,color:"var(--text)",letterSpacing:"-.02em"}}>
            {year}
          </div>
          <button aria-label="Año siguiente" onClick={()=>setYear(y=>y+1)} className="btn" style={navBtnS}>
            <Icon name="chevron-right" size={14} color="var(--text2)"/>
          </button>
          {isAdmin && (
            <button onClick={onImportar} className="btn" title="Importar periodos desde Excel"
              style={{padding:"5px 11px",borderRadius:8,border:"1px solid var(--border)",background:"var(--surface)",cursor:"pointer",fontSize:11.5,fontWeight:600,color:"var(--text2)",fontFamily:"var(--font)",display:"flex",alignItems:"center",gap:5}}>
              <Icon name="download" size={11} color="var(--text2)"/> Importar
            </button>
          )}
          <button onClick={()=>onNuevoPeriodo?.(fechaBase, personasVisibles)} className="btn" title="Vacaciones, permiso o baja"
            style={{padding:"5px 12px",borderRadius:8,border:"none",background:"var(--accent)",cursor:"pointer",fontSize:11.5,fontWeight:600,color:"var(--on-accent)",fontFamily:"var(--font)",display:"flex",alignItems:"center",gap:5}}>
            <Icon name="plus-lg" size={11} color="#fff"/> Pedir vacaciones
          </button>
        </div>

        <div className="cal-anio" style={{flex:1,overflowY:"auto"}}>
          {MONTHS_ES.map((nombre, mi) => (
            <div key={mi} style={{background:"var(--surface)",borderRadius:10,border:"1px solid var(--border)",padding:"9px 10px 10px"}}>
              <div style={{fontSize:11.5,fontWeight:800,color:"var(--text)",textAlign:"center",marginBottom:6}}>{nombre}</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(7,minmax(24px,1fr))",gap:2}}>
                {DAYS_INI.map((d,di) => (
                  <div key={di} style={{fontSize:11,fontWeight:700,color:"var(--text3)",textAlign:"center",paddingBottom:2}}>{d}</div>
                ))}
                {gridMes(year, mi).map((iso, ci) => {
                  if (!iso) return <div key={ci}/>;
                  const dia   = parseInt(iso.slice(8), 10);
                  const gente = porDia[iso] || [];
                  const fest  = festivos[iso];
                  const finde = esFinde(iso);
                  // Tres estados, tres aspectos. La progresión se lee sola: cuanto
                  // más cerca de ser firme, más relleno.
                  //
                  //   borrador    contorno a rayas, casi sin relleno  reservado
                  //   solicitada  relleno translúcido + aro entero    pedido
                  //   aprobada    color pleno                         firme
                  //
                  // El alfa va POR PERSONA y no como opacidad de la celda, para
                  // que un día con dos —una firme y otra pedida— distinga cada
                  // mitad de la diagonal.
                  const vistos = gente.slice(0,2);
                  const cols  = vistos.map(v => {
                    const c = colorUsuario(v.user_id, paleta);
                    return conAlfa(c, ALFA_ESTADO[v.estado] ?? 1);
                  });
                  // Solo si NINGUNA es firme cambia el texto y aparece el aro: con
                  // una aprobada debajo, la celda ya va en color pleno.
                  const blandas = vistos.length > 0 && vistos.every(v => !vacacionAprobada(v));
                  const soloBorrador = blandas && vistos.every(v => (v.estado || "aprobada") === "borrador");
                  // La trama solo se aplica con una persona: con dos, la celda ya
                  // va partida en diagonal y superponer rayas la haría ilegible.
                  const trama = gente.length === 1 ? tramaTipo(gente[0].tipo, cols[0]) : "none";
                  // Dos personas el mismo día → la celda se parte en diagonal
                  const fondo = cols.length === 0 ? (fest ? "rgba(239,68,68,.10)" : finde ? "var(--bg)" : "transparent")
                              : cols.length === 1 ? cols[0]
                              : `linear-gradient(135deg, ${cols[0]} 0 50%, ${cols[1]} 50% 100%)`;
                  // Sobre un fondo translúcido el blanco no se lee.
                  const texto = blandas ? "var(--text)"
                              : cols.length ? textoSobre(vistos.map(v => colorUsuario(v.user_id, paleta)), trama !== "none")
                              : fest ? "var(--red)" : finde ? "var(--text3)" : "var(--text2)";
                  // Un aro del color pleno: se ve que el día está cogido aunque el
                  // relleno sea casi transparente, y no depende de distinguir
                  // colores. Entero si está pedido, a rayas si es solo borrador.
                  const colorAro = blandas ? colorUsuario(vistos[0].user_id, paleta) : null;
                  const aro     = colorAro && !soloBorrador ? `inset 0 0 0 1.5px ${colorAro}` : "none";
                  const contorno = colorAro && soloBorrador ? `1.5px dashed ${colorAro}` : undefined;

                  return (
                    <div key={ci} {...clicable(()=>clickDia(iso), { label: etiquetaCelda(iso, fest, gente) })}
                      title={[fest && `Festivo: ${fest}`,
                              ...gente.map(v => `${v.user_name} · ${TIPO_LABEL[v.tipo]||v.tipo}`
                                + (ETIQUETA_ESTADO[v.estado] ? ` · ${ETIQUETA_ESTADO[v.estado]}` : "")
                                + (v.odoo_tipo_nombre ? ` (${v.odoo_tipo_nombre})` : ""))
                             ].filter(Boolean).join("\n") || undefined}
                      style={{
                        aspectRatio:"1", minWidth:24, minHeight:24, display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize:11, fontWeight: iso===hoy ? 800 : 500,
                        borderRadius:4, cursor:"pointer", background:fondo, color:texto,
                        backgroundImage: trama, boxShadow: aro,
                        outline: contorno, outlineOffset: contorno ? "-1.5px" : undefined,
                        textShadow: trama !== "none" && !blandas ? "0 1px 2px rgba(0,0,0,.5)" : "none",
                        border: iso===hoy ? "1.5px solid var(--accent)" : "1px solid transparent",
                        position:"relative",
                      }}>
                      {dia}
                      {gente.length > 2 && (
                        <span style={{position:"absolute",right:1,bottom:0,fontSize:11,fontWeight:800,color:"#fff"}}>
                          +{gente.length-2}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div style={{display:"flex",gap:14,fontSize:12,color:"var(--text3)",flexWrap:"wrap"}}>
          <span style={{color:"var(--text2)"}}>Pulsa un día para pedir vacaciones, un permiso o una baja, o para editar uno.</span>
          <span style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{width:8,height:8,borderRadius:2,background:"rgba(239,68,68,.4)",display:"inline-block"}}/> Festivo
          </span>
          <span style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{width:10,height:10,borderRadius:2,background:"#0e9f6e",display:"inline-block"}}/> Vacaciones
          </span>
          <span style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{width:10,height:10,borderRadius:2,display:"inline-block",background:"#0e9f6e",
              backgroundImage:"repeating-linear-gradient(90deg, #0e9f6e 0 5px, rgba(255,255,255,.42) 5px 8px)"}}/> Permiso
          </span>
          <span style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{width:10,height:10,borderRadius:2,display:"inline-block",background:"#0e9f6e",
              backgroundImage:"repeating-linear-gradient(135deg, #0e9f6e 0 5px, rgba(0,0,0,.38) 5px 10px)"}}/> Baja
          </span>
          <span style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{width:10,height:10,borderRadius:2,background:"linear-gradient(135deg,#0b7d96 0 50%,#f59e0b 50% 100%)",display:"inline-block"}}/> Dos personas
          </span>
        </div>
      </div>
    </div>
  );
}

const cajaS   = {background:"var(--surface)",borderRadius:12,border:"1px solid var(--border)",padding:"13px 13px 11px"};
const tituloS = {fontWeight:700,fontSize:11,color:"var(--text3)",textTransform:"uppercase",letterSpacing:".1em",marginBottom:9};
const filaChkS= {display:"flex",alignItems:"center",gap:7,cursor:"pointer",padding:"4px 0",fontSize:12,color:"var(--text2)"};
const navBtnS = {padding:"5px 10px",borderRadius:8,border:"1px solid var(--border)",background:"var(--surface)",cursor:"pointer",display:"flex",alignItems:"center"};
// Blanco u oscuro, el que mas contraste dé sobre TODOS los colores del fondo (4,5:1
// si la paleta lo permite). Con trama el fondo lleva rayas oscuras: se queda en blanco.
const lumin = hex => { const h = hex.replace("#",""); const [r,g,b] = [0,2,4].map(i => {
  const c = parseInt(h.slice(i,i+2),16)/255; return c <= .03928 ? c/12.92 : ((c+.055)/1.055)**2.4; });
  return .2126*r + .7152*g + .0722*b; };
const textoSobre = (colores, trama) => {
  if (trama || !colores.every(c => /^#[0-9a-f]{6}$/i.test(c))) return "#fff";
  const peor = fg => Math.min(...colores.map(c => { const a = lumin(fg), b = lumin(c);
    return (Math.max(a,b)+.05)/(Math.min(a,b)+.05); }));
  return peor("#111827") > peor("#ffffff") ? "#111827" : "#fff";
};
const linkS   = {background:"none",border:"none",padding:0,cursor:"pointer",fontSize:11,color:"var(--accent)",fontFamily:"var(--font)",fontWeight:600,minHeight:24,minWidth:24};
